// https://lit.dev/tutorials/tooltip/#10

import { html, LitElement } from "lit";
import { tooltipStyles } from "js/sharedStyles/tooltip.js";

import { query } from "lit/decorators.js";

import { Directive, directive } from "lit/directive.js";
import { render } from "lit";

// Positioning library
import { computePosition, autoPlacement, arrow, offset, flip, inline, shift } from "@floating-ui/dom";
/*
  https://floating-ui.com/docs/getting-started
*/
// Events to turn on/off the tooltip
const hasTouchCapability = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
const enterEvents = hasTouchCapability ? ["touchstart"] : ["mouseenter"];
const leaveEvents = ["mouseleave"];

export class BCTooltip extends LitElement {
  static properties = {
    showing: { reflect: true, type: Boolean },
    showCloseIcon: { reflect: true, type: Boolean },
    offset: { type: Number },
    side : { type: String, attribute: "data-tooltip-side" },
  };

  @query(".tooltip-arrow")
  _arrowElement;

  // Lazy creation
  static lazy(target, callback) {
    const createTooltip = () => {
      const tooltip = document.createElement("bc-tooltip");

      callback(tooltip);
      target.parentNode.insertBefore(tooltip, target.nextSibling);
      tooltip.show();
      // We only need to create the tooltip once, so ignore all future events.
      enterEvents.forEach((eventName) => target.removeEventListener(eventName, createTooltip));
    };
    ``;
    enterEvents.forEach((eventName) => target.addEventListener(eventName, createTooltip));
  }

  static styles = [tooltipStyles];

  constructor() {
    super();
    // Attribute for styling "showing"
    this.showing = false;
    // Position offset
    this.offset = 10;
  }

  connectedCallback() {
    super.connectedCallback();
    this.target ??= this.previousElementSibling;
  }

  _target = null;
  get target() {
    return this._target;
  }
  set target(target) {
    if (this.target) {
      enterEvents.forEach((name) => this.target.removeEventListener(name, this.show));
      leaveEvents.forEach((name) => this.target.removeEventListener(name, this.hide));
    }
    if (target) {
      // Add events to new target
      enterEvents.forEach((name) => target.addEventListener(name, this.show));
      leaveEvents.forEach((name) => target.addEventListener(name, this.hide));
    }

    this._target = target;
  }

  show = (e) => {
    this.showCloseIcon = e.type == "touchstart";
    this.style.cssText = "";
    this.style.display = "block";

    computePosition(this.target, this, {
      strategy: "absolute",
      middleware: [
        offset(this.offset),
        inline(),
        // flip(),
        shift(),
        autoPlacement({ allowedPlacements: ["top", "bottom", "right", "left"] }),
        arrow({
          element: this._arrowElement,
        }),
      ],
    }).then(({ x, y, middlewareData, placement }) => {
      Object.assign(this.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      if (middlewareData.arrow) {
        const { x, y } = middlewareData.arrow;

        Object.assign(this._arrowElement.style, {
          left: x != null ? `${x}px` : "",
          top: y != null ? `${y}px` : "",
        });

        this._arrowElement.classList.remove("on-top","on-bottom","on-left","on-right");
        this._arrowElement.classList.add("on-"+placement);

      }
    });
    this.showing = true;
  };

  hide = () => {
    this.showing = false;
    this.style.display = "none";
  };
  

  render() {
    return html`
      <button ?hidden=${!this.showCloseIcon} class="c-icon-close-tooltip" @click=${() => this.hide()}></button>
      <slot></slot>
      <div id="arrow" class="tooltip-arrow"></div>
    `;
  }
}

class TooltipDirective extends Directive {
  didSetupLazy = false;
  tooltipContent;
  part;
  tooltip;

  render(tooltipContent = "") {}

  update(part, [tooltipContent]) {
    this.tooltipContent = tooltipContent;
    this.part = part;
    if (!this.didSetupLazy) {
      this.setupLazy();
    }
    if (this.tooltip) {
      this.renderTooltipContent();
    }
  }

  setupLazy() {
    this.didSetupLazy = true;
    BCTooltip.lazy(this.part.element, (tooltip) => {
      this.tooltip = tooltip;
      this.renderTooltipContent();
    });
  }

  renderTooltipContent() {
    render(this.tooltipContent, this.tooltip, this.part.options);
  }
}
const initBCTooltip = () => {
  if (!window.customElements.get("bc-tooltip")) {
    customElements.define("bc-tooltip", BCTooltip);
  }
};

const tooltip = directive(TooltipDirective);
export { initBCTooltip, tooltip };
